import React, { useMemo, useState } from "react";
import moment from "moment";
import { useQuery, useQueryClient } from "react-query";

import { Button } from "components/Button";
import { Input } from "components/Input";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { MediumText, RegularText } from "components/Text";
import { VerticalSpace, HorizontalSpace } from "components/Spacing";

import { getErrorMessage } from "services/api";

import {
  getClientTransactions,
  createClientTransaction,
  deleteClientTransaction,
  getTransactionCalculation,
} from "./api";
import styles from "./styles.module.scss";

/** Блок «Внести/изъять бонусы» */
export const ClientBonusOperations = ({ clientid }) => {
  const queryClient = useQueryClient();

  const calculationQuery = useQuery(["transaction-calculation", clientid], () =>
    getTransactionCalculation(clientid)
  );
  const accumulated =
    calculationQuery.data?.total ?? calculationQuery.data ?? 0;

  const [add, setAdd] = useState("");
  const [reduce, setReduce] = useState("");
  const [comment, setComment] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const runTransaction = async (total, action) => {
    if (!total) return;
    setSaving(true);
    setError("");
    try {
      await createClientTransaction({
        total,
        comment,
        clientid,
        action,
      });
      setAdd("");
      setReduce("");
      setComment("");
      queryClient.invalidateQueries(["client-transactions", clientid]);
      queryClient.invalidateQueries(["transaction-calculation", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className={styles.bonusTotalRow}>
        <RegularText>Накопленные бонусы:</RegularText>
        <HorizontalSpace size={8} />
        <MediumText color="primary">{String(accumulated)}</MediumText>
      </div>
      <VerticalSpace size={12} />
      <div className={styles.bonusOperationRow}>
        <Input
          type="number"
          value={add}
          onChange={(ev) => setAdd(ev.target.value)}
          inputWidth={120}
        />
        <Button
          variant="secondary"
          onClick={() => runTransaction(add, "add")}
          disabled={!add || saving}
        >
          Начислить
        </Button>
        <HorizontalSpace size={16} />
        <Input
          type="number"
          value={reduce}
          onChange={(ev) => setReduce(ev.target.value)}
          inputWidth={120}
        />
        <Button
          onClick={() => runTransaction(reduce, "reduce")}
          disabled={!reduce || saving}
        >
          Списать
        </Button>
      </div>
      <VerticalSpace size={12} />
      <Input
        label="Комментарий"
        labelWidth={100}
        value={comment}
        onChange={(ev) => setComment(ev.target.value)}
        inputWidth={400}
      />
      {!!error && (
        <>
          <VerticalSpace size={8} />
          <RegularText size={13} color="danger">
            {error}
          </RegularText>
        </>
      )}
    </div>
  );
};

/** История операций по бонусам (только для существующего клиента) */
export const ClientTransactionsSection = ({ clientid }) => {
  const queryClient = useQueryClient();

  const transactionsQuery = useQuery(["client-transactions", clientid], () =>
    getClientTransactions(clientid)
  );
  const transactions = useMemo(
    () => transactionsQuery.data?.items ?? [],
    [transactionsQuery.data]
  );

  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [error, setError] = useState("");

  const filtered = useMemo(() => {
    if (!search) return transactions;
    const lower = search.toLowerCase();
    return transactions.filter((t) =>
      [t.comment, t.total, t.time, t.clientid]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(lower))
    );
  }, [transactions, search]);

  const rowData = useMemo(
    () =>
      filtered.map((t) => ({
        clienttransactionid: t.clienttransactionid,
        time: t.time ? moment(t.time).format("DD.MM.YYYY") : "",
        total: t.total,
        comment: t.comment,
      })),
    [filtered]
  );

  const colDefs = useMemo(
    () => [
      { field: "clienttransactionid", headerName: "№", width: 90 },
      { field: "time", headerName: "Дата", flex: 2 },
      { field: "total", headerName: "Сумма", flex: 1 },
      { field: "comment", headerName: "Комментарий", flex: 5 },
    ],
    []
  );

  const handleDelete = async () => {
    if (!selectedId) return;
    setError("");
    try {
      await deleteClientTransaction(selectedId);
      setSelectedId(null);
      queryClient.invalidateQueries(["client-transactions", clientid]);
      queryClient.invalidateQueries(["transaction-calculation", clientid]);
    } catch (e) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <div>
      <SearchInput value={search} onChange={setSearch} placeholder="Поиск" />
      <VerticalSpace size={8} />
      <Table
        height={300}
        rowData={rowData}
        colDefs={colDefs}
        rowKey="clienttransactionid"
        selectedId={selectedId}
        onSelect={(row) =>
          setSelectedId((prev) =>
            prev === row.clienttransactionid ? null : row.clienttransactionid
          )
        }
        loading={transactionsQuery.isLoading}
        error={
          transactionsQuery.isError
            ? getErrorMessage(transactionsQuery.error)
            : ""
        }
        onRetry={transactionsQuery.refetch}
      />
      {!!error && (
        <>
          <VerticalSpace size={8} />
          <RegularText size={13} color="danger">
            {error}
          </RegularText>
        </>
      )}
      <VerticalSpace size={8} />
      <div className={styles.sectionActions}>
        <Button
          icon="refresh"
          variant="secondary"
          onClick={() => transactionsQuery.refetch()}
        >
          Обновить
        </Button>
        <Button icon="delete" onClick={handleDelete} disabled={!selectedId}>
          Удалить
        </Button>
      </div>
    </div>
  );
};
